/**
 * The jobs list (DESIGN UD-21 jobs list): one card per job, newest first, so a customer can tell which one is which. Each card
 * carries the document's name (and, when the name does not tell it apart, its opening words), the local date and time the job started, a coloured status badge (running, paused, done,
 * cancelled, error), the stretches done of total (a small bar while the job is unfinished), the route, model and effort, the
 * spend so far, and the actions: Open (a finished job: the before/after overview and the lens) or Resume, and Remove; the
 * job this page is running has Details (its live status) instead. The
 * order is the store's (`listJobs`: newest `createdAt` first). Text nodes only.
 */
import { fmtInt } from '../format.ts';
import { JOBS, VERSIONS } from '../strings-bench.ts';
import { actionTitle, jobAction } from './action-label.ts';
import { adoptJob } from './chain-control.ts';
import { canAdopt, jobPlace } from './chain.ts';
import { openJob, openRunSummary, removeJobAction } from './controller.ts';
import type { BenchJob } from './job.ts';
import {
	fmtJobTime,
	jobCost,
	jobDeclined,
	jobExcerpt,
	jobFraction,
	jobName,
	jobProgress,
	jobSetup,
	stopText,
} from './jobs-format.ts';
import { chain, jobs, jobsElsewhere, liveJobId } from './state.ts';

/** The job's place in the open document's chain: the version it started from, and the one it made or why it made none. */
function placeText(j: BenchJob): string | null {
	const p = jobPlace(chain.value, j);
	const base = p.base === 0 ? VERSIONS.place.onOriginal : VERSIONS.place.onVersion(String(p.base));
	if (p.made !== null) return `${base} · ${VERSIONS.place.made(String(p.made))}`;
	if (p.why === 'other-document') return null;
	return `${base} · ${VERSIONS.place[p.why]}`;
}

function JobCard({ job: j }: { job: BenchJob }) {
	const { name, hash } = jobName(j);
	const when = fmtJobTime(j.createdAt);
	const unfinished = j.status === 'running' || j.status === 'paused';
	const cost = jobCost(j);
	const source = j.sourceKind ? JOBS.sources[j.sourceKind] : null;
	const done = j.status === 'done';
	// the job this page is running: its live status, not Resume or Remove (a second run of it would run beside the first)
	const live = j.id === liveJobId.value;
	// a Standard job another tab is running (its run lock is held there, UD-38): shown as such, with no Resume or Remove here
	const elsewhere = !live && jobsElsewhere.value.has(j.id);
	const stopped = stopText(j);
	const excerpt = jobExcerpt(j);
	const place = placeText(j);
	const declinedCount = jobDeclined(j);
	const step = jobAction(j);
	const action = step ? actionTitle(step) : null;
	const adoptable = chain.value !== null && canAdopt(chain.value, j);
	return (
		<li class="job-card" data-testid="bench-job" data-status={j.status} data-job-id={j.id}>
			<div class="job-main">
				<div class="job-head">
					<span class="job-name" data-testid="bench-job-name">
						{name}
						{hash ? <span class="job-hash mono"> {hash}</span> : null}
					</span>
					<span class="job-badge" data-status={j.status} data-testid="bench-job-status">
						{elsewhere ? JOBS.elsewhere : JOBS[j.status]}
					</span>
				</div>
				{elsewhere ? (
					<p class="job-stop" data-testid="bench-job-elsewhere">
						{JOBS.elsewhereNote}
					</p>
				) : stopped ? (
					<p class="job-stop" data-testid="bench-job-stop">
						{stopped}
					</p>
				) : null}
				{action ? (
					<p class="job-action" data-testid="bench-job-action">
						{action}
					</p>
				) : null}
				{declinedCount > 0 ? (
					<p class="job-declined dim" data-testid="bench-job-declined">
						{JOBS.declined(fmtInt(declinedCount))}
					</p>
				) : null}
				{excerpt ? (
					<p class="job-excerpt" data-testid="bench-job-excerpt">
						{JOBS.excerpt(excerpt)}
					</p>
				) : null}
				{/* the clip hides the separator a wrapped line would start with */}
				<div class="job-meta-clip">
					<div class="job-meta">
						<time dateTime={j.createdAt} data-testid="bench-job-time" title={JOBS.started(when)}>
							{when}
						</time>
						<span>{jobSetup(j)}</span>
						{source ? <span>{source}</span> : null}
						{j.words !== undefined ? <span>{JOBS.words(fmtInt(j.words))}</span> : null}
						{cost ? <span>{cost}</span> : null}
					</div>
				</div>
				{place ? (
					<p class="job-place" data-testid="bench-job-place">
						{place}
					</p>
				) : null}
				<div class="job-progress">
					{unfinished || j.status === 'error' || j.status === 'cancelled' ? (
						<progress
							class="job-bar"
							value={j.doneChunks}
							max={Math.max(1, j.totalChunks)}
							aria-label={JOBS.progressLabel(name)}
							data-fraction={jobFraction(j).toFixed(2)}
						/>
					) : null}
					<span class="dim" data-testid="bench-job-progress">
						{jobProgress(j)}
					</span>
				</div>
			</div>
			<div class="job-actions">
				{live ? (
					<button
						type="button"
						class="btn btn-sm btn-primary"
						aria-label={JOBS.detailsLabel(name, when)}
						data-testid="bench-job-details"
						onClick={openRunSummary}
					>
						{JOBS.details}
					</button>
				) : (
					<>
						<button
							type="button"
							class={`btn btn-sm ${done ? 'btn-primary' : 'btn-secondary'}`}
							aria-label={done ? JOBS.openLabel(name, when) : JOBS.resumeLabel(name, when)}
							disabled={elsewhere}
							title={elsewhere ? JOBS.elsewhereTitle : undefined}
							onClick={() => void openJob(j)}
						>
							{done ? JOBS.open : JOBS.resume}
						</button>
						<button
							type="button"
							class="btn btn-secondary btn-sm"
							aria-label={JOBS.removeLabel(name, when)}
							disabled={elsewhere}
							title={elsewhere ? JOBS.elsewhereTitle : undefined}
							onClick={() => void removeJobAction(j)}
						>
							{JOBS.remove}
						</button>
						{adoptable && !elsewhere ? (
							<button
								type="button"
								class="btn btn-secondary btn-sm"
								aria-label={VERSIONS.adoptLabel(name, when)}
								data-testid="bench-job-adopt"
								onClick={() => void adoptJob(j)}
							>
								{VERSIONS.adopt}
							</button>
						) : null}
					</>
				)}
			</div>
		</li>
	);
}

export function JobsList() {
	const list = jobs.value;
	if (list.length === 0) return <p class="dim">{JOBS.none}</p>;
	return (
		<ul class="jobs-list" data-testid="bench-jobs-list">
			{list.map((j) => (
				<JobCard key={j.id} job={j} />
			))}
		</ul>
	);
}
